import React from 'react';

import {
  doBoxSchemasWithRegistryQuery,
  doContainerSchemasWithRegistryQuery,
  doLocationSchemasWithRegistryQuery,
  doPlateSchemasWithRegistryQuery,
} from 'inventory/useStorableSchemasQuery';
import EditableLookupConfigDisplay from 'lookup-config/EditableLookupConfigDisplay';
import LookupConfigModal from 'lookup-config/LookupConfigModal';
import {
  DEFAULT_FINAL_LOOKUP_STEP_TYPES,
  DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
  LookupConfigSpec,
  LookupStepType,
} from 'lookup-config/LookupConfigModalConstants';
import {RunLookupConfigModalProps} from 'lookup-config/LookupConfigUtils';
import {isLookupConfigValidForModal} from 'lookup-config/LookupStepInitializerUtils';
import {doAllProcessTaskSchemasForSchemaConfigQuery} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {ProcessTaskSchema} from 'processes/settings/ProcessTaskSchemaUtils';
import {doAllResultSchemasQuery} from 'results/useAllResultSchemasQuery';
import {doAllSchemaInterfacesQuery} from 'schemas/util/useAllSchemaInterfacesQuery';
import {doTagSchemasWithRegistryQuery} from 'schemas/util/useTagSchemasQuery';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';

type ProcessOutputSchema = NonNullable<ProcessTaskSchema['outputSchema']>;

// exported for test only
const baseTestAttrs = {component: 'ConfigureTaskFinder'};
export const DATA_TEST_ATTRS = {
  taskFinderSet: {...baseTestAttrs, element: 'set'},
  taskFinderEdit: {...baseTestAttrs, element: 'edit'},
  taskFinderRemove: {...baseTestAttrs, element: 'remove'},
  taskFinderLookupConfigDescription: {...baseTestAttrs, element: 'lookupConfigDescription'},
};

export async function runLookupConfigModalForProcessTaskFinder(
  modalProps: RunLookupConfigModalProps
): Promise<void> {
  // Ensure all schemas are loaded
  const supportingData = Promise.all([
    doAllResultSchemasQuery(),
    doBoxSchemasWithRegistryQuery(),
    doContainerSchemasWithRegistryQuery(),
    doLocationSchemasWithRegistryQuery(),
    doPlateSchemasWithRegistryQuery(),
    doTagSchemasWithRegistryQuery(),
    doAllSchemaInterfacesQuery(),
    doAllProcessTaskSchemasForSchemaConfigQuery(),
  ]);
  await withLoadingScreen(supportingData);
  if (isLookupConfigValidForModal(modalProps)) {
    runModal(LookupConfigModal, modalProps);
  } else {
    throw new Error(`Lookup is not supported in UI but no JSON editor is available`);
  }
}

interface ConfigureTaskFinderProps {
  taskFinderLookupConfig: LookupConfigSpec | null;
  outputSchema: ProcessOutputSchema;
  taskSchema: ProcessTaskSchemaGraphQL;
  isArchived?: boolean;
  onChange: (newLookup: LookupConfigSpec | null) => void;
  locked?: boolean;
}

export default function ConfigureTaskFinderButton(props: ConfigureTaskFinderProps): JSX.Element {
  const context = {processTaskSchema: props.taskSchema};
  if (props.taskFinderLookupConfig) {
    return (
      <EditableLookupConfigDisplay
        baseSchema={props.outputSchema}
        lookupConfig={props.taskFinderLookupConfig}
        context={context}
        onEdit={() => {
          runLookupConfigModalForProcessTaskFinder({
            baseSchema: props.outputSchema,
            firstLookupStepTypes: [LookupStepType.SCHEMA_FIELD],
            intermediateLookupStepTypes: DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
            finalLookupStepTypes: DEFAULT_FINAL_LOOKUP_STEP_TYPES,
            initialLookupConfig: props.outputSchema.taskFinderLookupConfig,
            onRequestSubmit: ({lookupConfig}) => {
              props.onChange(lookupConfig);
            },
            submitButtonText: 'Done',
            context,
          });
        }}
        onRemove={() => {
          props.onChange(null);
        }}
        lookupType="output → task lookup"
        isDisabled={props.isArchived || props.locked}
        editTestAttrs={DATA_TEST_ATTRS.taskFinderEdit}
        removeTestAttrs={DATA_TEST_ATTRS.taskFinderRemove}
        lookupConfigDescriptionTestAttrs={DATA_TEST_ATTRS.taskFinderLookupConfigDescription}
      />
    );
  } else {
    let errorText = null;
    if (props.outputSchema.fields.length === 0) {
      errorText = 'Output schema must have at least one field';
    } else if (props.taskSchema.fields.length === 0) {
      errorText = 'Task schema must have at least one field';
    }
    const button = (
      <Button
        size={ButtonSize.SMALL}
        disabled={Boolean(props.isArchived || errorText != null || props.locked)}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        onClick={() => {
          runLookupConfigModalForProcessTaskFinder({
            baseSchema: props.outputSchema,
            firstLookupStepTypes: [LookupStepType.SCHEMA_FIELD],
            intermediateLookupStepTypes: DEFAULT_INTERMEDIATE_LOOKUP_STEP_TYPES,
            finalLookupStepTypes: DEFAULT_FINAL_LOOKUP_STEP_TYPES,
            onRequestSubmit: ({lookupConfig}) => {
              props.onChange(lookupConfig);
            },
            submitButtonText: 'Done',
            context: {processTaskSchema: props.taskSchema},
          });
        }}
        dataTestAttrs={DATA_TEST_ATTRS.taskFinderSet}
        text="Configure"
      />
    );
    return errorText != null ? <Tooltip title={errorText}>{button}</Tooltip> : button;
  }
}
